<!-- --vh modifier while the Chat tab is active: pins the slider to the
     viewport height so the thread scrolls internally and the input row
     stays a fixed footer (see job-center-v3.scss). -->
<div class="jc-v3-cps-slider" [class.jc-v3-cps-slider--vh]="activeTab === 'chat'">

  <!-- Sticky header (avatar / name / match ring / actions) -->
  <app-cp-header
    direction="candidate"
    [title]="candidateName"
    [titleBadge]="titleBadge"
    [upgradeBadge]="upgradeBadge"
    [subtitle]="candidate?.jobTitle || candidate?.title"
    [refId]="candidateCid"
    [meta]="headerMeta"
    [matchScore]="matchScore"
    [isCeipalFreePlan]="isCeipalFreePlan"
    [hideMatchRing]="aiHidden"
    matchLabel="Match"
    [initials]="candidateInitials"
    [photoUrl]="candidatePhoto"
    [actions]="headerActions"
    (matchRingClick)="onMatchRingClick()"
    (closeClick)="onClose()"
    (actionClick)="onHeaderAction($event)">
  </app-cp-header>

  <!-- Submitter strip — Connect / Chat actions reuse the same
       connection-template the V3 details page uses (new-candidate.html
       lines 311-317), so all status states (NOT_CONNECTED, REQUEST_SENT,
       REQUEST_RECEIVED, CONNECTED) and their button copy stay in lockstep
       with the rest of the app. -->
  <app-cp-rec-strip
    prefix="Submitted by"
    [name]="isOwnCandidate ? 'You' : submitterName"
    [nameForInitials]="submitterName"
    [role]="submitterRole"
    [organisation]="submitterOrganisation"
    [photoUrl]="submitterPhoto">
    <!-- Connect / personal-chat hidden for the viewer's own candidate on ANY
         page (Talent Center, Job → Applied, …) — you can't connect or 1-to-1
         chat with your own candidate. The header "Chat" (group) action is
         unaffected. -->
    <app-connection-template actions
      *ngIf="!isOwnCandidate"
      [page]="'jc-v3-cps-rec-strip'"
      [connectionStatus]="candidate?.connectionStatus"
      [counterpartsDetails]="candidate?.user"
      [values]="candidate"
      [module]="'candidate'"
      [hideChatUntilConnected]="true">
    </app-connection-template>
  </app-cp-rec-strip>

  <!-- Tab bar — 4 tabs per the wireframe (gigsumo_job_center_v2(16).html
       line 1724-1729): Candidate Profile / Full Resume / AI Insights / Chat.
       The header "Chat" action button also lands on the Chat tab (the
       wireframe's header button does switchCpTab('chat')). -->
  <nav class="jc-v3-cps-tabs" role="tablist">
    <button type="button" class="jc-v3-cps-tab"
            [class.active]="activeTab === 'profile'"
            (click)="selectTab('profile')">Candidate Profile</button>
    <button type="button" class="jc-v3-cps-tab"
            [class.active]="activeTab === 'resume'"
            (click)="selectTab('resume')">Full Resume</button>
    <button type="button" class="jc-v3-cps-tab"
            *ngIf="!aiHidden"
            [class.active]="activeTab === 'ai'"
            (click)="isCeipalFreePlan ? showCeipalFreePopup() : selectTab('ai')">
      <ng-icon name="tablerBolt"></ng-icon>
      AI Insights
    </button>
    <button type="button" class="jc-v3-cps-tab"
            *ngIf="!chatHidden"
            [class.active]="activeTab === 'chat'"
            [disabled]="chatDisabled"
            (click)="openChatTab()">
      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      <span class="jc-v3-cps-tab-chat">
        Chat
        <span class="jc-v3-cps-tab-count"
              *ngIf="candidate?.nonSeenMessageCount > 0">{{ candidate.nonSeenMessageCount }}</span>
      </span>
    </button>
  </nav>

  <!-- Tab bodies — every one lazy-rendered via *ngIf so Chat WebSocket
       and AI fetch only fire when their tab is active. -->
  <div class="jc-v3-cps-panes">

    <!-- Tab 1 — Candidate Profile -->
    <section class="jc-v3-cps-pane" *ngIf="activeTab === 'profile'">
      <!-- Submitted For / Pay Rate card — mock parity
           (gigsumo_job_center_v2(16).html lines 1738-1744).
           Layout strategy:
             - Both present  → left "Submitted for: X", right "Pay Rate: range"
             - Only payRate  → left "Pay Rate: range", right hidden
             - Only submitted→ left "Submitted for: X",  right hidden
           Card hides only when both are empty. -->
      <div class="jc-v3-cps-submitted" *ngIf="submittedForDisplay || payRateRange">
        <div class="jc-v3-cps-submitted-row">
          <div class="jc-v3-cps-submitted-main">
            <span class="jc-v3-cps-submitted-ico" aria-hidden="true">$</span>
            <span class="jc-v3-cps-submitted-label">{{ submittedForDisplay ? 'Submitted for:' : 'Pay Rate:' }}</span>
            <strong class="jc-v3-cps-submitted-rate">{{ submittedForDisplay || payRateRange }}</strong>
          </div>
          <div class="jc-v3-cps-submitted-aux"
               *ngIf="submittedForDisplay && payRateRange">
            Pay Rate: {{ payRateRange }}
          </div>
        </div>
      </div>

      <!-- Quick Facts card — wireframe wraps title + info grid + tag row
           inside a single bordered surface. -->
      <div class="jc-v3-cps-card">
        <div class="jc-v3-cps-section-title jc-v3-cps-quickfacts-title">Quick Facts</div>

        <!-- Every Quick Facts item always renders its header; missing values
             show a "-" placeholder instead of hiding the row (user request,
             applies to the job slider's Quick Facts too). Icons/accents only
             render alongside a real value so the dash stays plain. -->
        <div class="jc-v3-cps-info-grid">
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Experience</div>
            <div class="jc-v3-cps-info-value">
              <ng-container *ngIf="candidate?.totalExperience; else dash">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                {{ candidate?.totalExperience }} Years
              </ng-container>
            </div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Availability</div>
            <div class="jc-v3-cps-info-value"
                 [class.jc-v3-cps-info-value--accent-green]="candidate?.availableIn">
              <ng-container *ngIf="candidate?.availableIn; else dash">
                <span aria-hidden="true" style="font-size:13px;line-height:1;">⚡</span>
                {{ candidate?.availableIn }}
              </ng-container>
            </div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Engagement Type</div>
            <div class="jc-v3-cps-info-value">{{ candidate?.engagementType || '-' }}</div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Work Authorization</div>
            <div class="jc-v3-cps-info-value">{{ candidate?.workAuthorization || '-' }}</div>
          </div>
          <!-- Notice Period — payload ships no dedicated `noticePeriod` field;
               per wireframe it mirrors the availability value (`availableIn`,
               same source as the Availability item above). A real noticePeriod
               still wins if the backend ever sends one. -->
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Notice Period</div>
            <div class="jc-v3-cps-info-value">{{ candidate?.noticePeriod || candidate?.availableIn || '-' }}</div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Current Employer</div>
            <div class="jc-v3-cps-info-value">{{ currentEmployer || '-' }}</div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Candidate ID</div>
            <div class="jc-v3-cps-info-value"
                 [class.jc-v3-cps-info-value--accent-purple]="candidate?.candidateReferenceId || candidate?.candidateId">
              {{ candidate?.candidateReferenceId || candidate?.candidateId || '-' }}
            </div>
          </div>
          <div class="jc-v3-cps-info-item">
            <div class="jc-v3-cps-info-label">Created On</div>
            <div class="jc-v3-cps-info-value">{{ candidate?.updatedOn ? (candidate?.updatedOn | date: 'MM/dd/y') : '-' }}</div>
          </div>
        </div>
        <ng-template #dash>-</ng-template>

        <!-- Tag row always renders the Security Clearance pill so the
             recruiter sees the clearance state at a glance:
               true  → green pill "Security Clearance"
               false → red pill "✕ Security Clearance"
             Work-mode / Resume pills remain conditional on their data. -->
        <div class="jc-v3-cps-tag-row" *ngIf="candidate">
          <span class="jc-v3-cps-tag" data-variant="gray" *ngIf="candidate?.workMode">{{ candidate?.workMode }}</span>
          <span class="jc-v3-cps-tag"
                [attr.data-variant]="candidate?.securityClearance ? 'green' : 'red'">
            <span *ngIf="!candidate?.securityClearance" class="jc-v3-cps-tag-ico" aria-hidden="true">✕</span>
            Security Clearance
          </span>
          <span class="jc-v3-cps-tag" data-variant="purple" *ngIf="candidate?.resumes?.length > 0">Resume Available</span>
        </div>
      </div>

      <!-- Skills card — wireframe (image 8) groups Primary + Secondary skills
           inside one bordered surface, with each subsection keeping its own
           uppercase label. -->
      <div class="jc-v3-cps-card" *ngIf="primarySkills.length || secondarySkills.length">
        <section class="jc-v3-cps-section" *ngIf="primarySkills.length">
          <h4 class="jc-v3-cps-section-title">Primary Skills</h4>
          <div class="jc-v3-cps-chips">
            <span class="jc-v3-cps-chip jc-v3-cps-chip-primary"
                  *ngFor="let s of primarySkills; trackBy: trackByIndex">{{ s }}</span>
          </div>
        </section>
        <section class="jc-v3-cps-section">
          <h4 class="jc-v3-cps-section-title">Secondary Skills</h4>
          <div class="jc-v3-cps-chips">
            <ng-container *ngIf="secondarySkills.length; else emptySecondarySkills">
              <span class="jc-v3-cps-chip jc-v3-cps-chip-secondary"
                    *ngFor="let s of secondarySkills; trackBy: trackByIndex">{{ s }}</span>
            </ng-container>
            <ng-template #emptySecondarySkills>
              <span class="text-muted">–</span>
            </ng-template>
          </div>
        </section>
      </div>

      <!-- Candidate Summary card — backend ships the rich HTML under
           `resumeSummary` (the SAME field the V2
           candidate-profile-summary renders at
           candidate-profile-summary.component.html:146-148). The earlier
           `aboutCandidate` binding never matched a real field, so the
           card was always hidden. The candidateSummaryHtml getter resolves
           resumeSummary (with aboutCandidate fallback) and blanks empty
           summernote placeholders. -->
      <div class="jc-v3-cps-card" *ngIf="candidateSummaryHtml">
        <section class="jc-v3-cps-section">
          <h4 class="jc-v3-cps-section-title">Candidate Summary</h4>
          <!-- Rendered with [ngxSummernoteView] to match the V2
               candidate-profile-summary exactly (component .html:148) so
               Summernote formatting (lists, bold, line breaks) displays
               identically to the existing slider. -->
          <div class="jc-v3-cps-summary-text" style="word-wrap:break-word;"
               [ngxSummernoteView]="candidateSummaryHtml"></div>
        </section>
      </div>

      <!-- Work Experience card — field names mirror the V2 details page
           (candidates-profile-details.component.html lines 68-117):
           title / organisationName / city,state,country /
           startMonth,startYear,endMonth,endYear, currentOrganization. -->
      <div class="jc-v3-cps-card" *ngIf="candidate?.workExperiences?.length">
        <section class="jc-v3-cps-section">
          <h4 class="jc-v3-cps-section-title">Work Experience</h4>
          <ul class="jc-v3-cps-timeline">
            <li class="jc-v3-cps-timeline-item"
                *ngFor="let exp of sortedWorkExperiences; trackBy: trackByIndex">
              <div class="jc-v3-cps-timeline-dot"></div>
              <div class="jc-v3-cps-timeline-body">
                <div class="jc-v3-cps-timeline-title">{{ exp.title }}</div>
                <div class="jc-v3-cps-timeline-sub" *ngIf="exp.organisationName">{{ exp.organisationName }}</div>
                <div class="jc-v3-cps-timeline-sub" *ngIf="exp.city || exp.state">
                  <span *ngIf="exp.city">{{ exp.city }}</span><span *ngIf="exp.city && exp.state">, </span><span *ngIf="exp.state">{{ exp.state }}</span>
                </div>
                <div class="jc-v3-cps-timeline-duration"
                     *ngIf="exp.startMonth && exp.startYear">
                  {{ exp.startMonth?.substring(0, 3) }} {{ exp.startYear }} –
                  <ng-container *ngIf="exp.currentOrganization; else expEndDate">Present</ng-container>
                  <ng-template #expEndDate>
                    <ng-container *ngIf="exp.endMonth && exp.endYear">
                      {{ exp.endMonth?.substring(0, 3) }} {{ exp.endYear }}
                    </ng-container>
                  </ng-template>
                  <span class="jc-v3-cps-tenure" *ngIf="getExperienceDuration(exp) as dur"> · {{ dur }}</span>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <!-- Education card — field names mirror the V2 details page
           (candidates-profile-details.component.html lines 161-199):
           schoolName / degree / city,state,country /
           startMonth,startYear,endMonth,endYear, currentlyPursued. -->
      <div class="jc-v3-cps-card" *ngIf="candidate?.educationDetails?.length">
        <section class="jc-v3-cps-section">
          <h4 class="jc-v3-cps-section-title">Education</h4>
          <ul class="jc-v3-cps-timeline">
            <li class="jc-v3-cps-timeline-item"
                *ngFor="let ed of sortedEducation; trackBy: trackByIndex">
              <div class="jc-v3-cps-timeline-dot" style="background:#3B82F6"></div>
              <div class="jc-v3-cps-timeline-body">
                <div class="jc-v3-cps-timeline-title" *ngIf="ed.degree || ed.schoolName">{{ ed.degree || ed.schoolName }}</div>
                <div class="jc-v3-cps-timeline-sub" *ngIf="ed.degree && ed.schoolName">{{ ed.schoolName }}</div>
                <div class="jc-v3-cps-timeline-sub" *ngIf="ed.city || ed.state">
                  <span *ngIf="ed.city">{{ ed.city }}</span><span *ngIf="ed.city && ed.state">, </span><span *ngIf="ed.state">{{ ed.state }}</span>
                </div>
                <div class="jc-v3-cps-timeline-duration"
                     *ngIf="ed.startMonth && ed.startYear">
                  {{ ed.startMonth?.substring(0, 3) }} {{ ed.startYear }} –
                  <ng-container *ngIf="ed.currentlyPursued; else edEndDate">Present</ng-container>
                  <ng-template #edEndDate>
                    <ng-container *ngIf="ed.endMonth && ed.endYear">
                      {{ ed.endMonth?.substring(0, 3) }} {{ ed.endYear }}
                    </ng-container>
                  </ng-template>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <!-- Certifications card — wireframe (image 10) renders certs as a
           chip row, NOT a timeline. Keep just the cert name on the chip. -->
      <div class="jc-v3-cps-card" *ngIf="candidate?.candidateCertifications?.length">
        <section class="jc-v3-cps-section">
          <h4 class="jc-v3-cps-section-title">Certifications</h4>
          <div class="jc-v3-cps-chips">
            <span class="jc-v3-cps-chip jc-v3-cps-chip-cert"
                  *ngFor="let c of candidate?.candidateCertifications; trackBy: trackByIndex">
              {{ c.certificationName }}
            </span>
          </div>
        </section>
      </div>

      <!-- Fallback loading state -->
      <div class="jc-v3-cps-loading" *ngIf="loadingProfile && !fullProfile">
        Loading full profile…
      </div>
    </section>

    <!-- Tab 2 — Full Resume -->
    <section class="jc-v3-cps-pane" *ngIf="activeTab === 'resume'">
      <app-cp-resume-card [candidate]="candidate"
                          [downloading]="resumeDownloading"
                          (downloadClick)="downloadPrimaryResume()"></app-cp-resume-card>
    </section>

    <!-- Tab 3 — AI Insights. `variant="wireframe"` enables the
         purple-gradient score card + match-breakdown label + outlined
         feedback buttons (mock parity per gigsumo_job_center_v2(16)
         AI tab). Job slider stays on the default look for now. -->
    <section class="jc-v3-cps-pane" *ngIf="activeTab === 'ai' && !aiHidden">
      <app-cp-ai-insights
        [pageFrom]="pageFrom"
        [jobId]="jobId"
        [candidateId]="candidate?.candidateId"
        [variant]="'wireframe'">
      </app-cp-ai-insights>
    </section>

    <!-- Tab 4 — Chat (wireframe TAB 4, mock line 1933). The message widget
         (normally a fixed bottom-right bubble) is re-homed to fill this pane
         via the .jc-v3-cps-pane-chat overrides in job-center-v3.scss. The
         plan-check → connection-check gate runs in openChatTab() before
         messagemodelflag flips, identical to the old header-button flow;
         bindings still mirror `candidate-card-table-view.html`. Closing via
         the widget's X returns to the Candidate Profile tab. -->
    <!-- <section class="jc-v3-cps-pane jc-v3-cps-pane-chat" *ngIf="activeTab === 'chat'"> -->
    <section class="jc-v3-cps-pane jc-v3-cps-pane-chat" [hidden]="activeTab !=='chat'">

      <!-- Initializing (plan/connection gate + widget's 1s reset delay):
    <section class="jc-v3-cps-pane jc-v3-cps-pane-chat" *ngIf="activeTab === 'chat'">
      <!-- Initializing (plan/connection gate + widget's 1s reset delay):
           same 2-pair chat skeleton the widget shows while history loads,
           so the pane keeps one continuous loading look. -->
      <!-- <div class="jc-v3-cps-chat-loading" *ngIf="!messagemodelflag">
        <app-common-skeleton class="chat-skel-v3" [NameOftheScreen]="'messageConversation'" [countforView]="2"></app-common-skeleton>
      </div> -->
      <div class="jc-v3-cps-chat-loading" *ngIf="!messagemodelflag">
  <app-common-skeleton *ngIf="chatLoadingIsOwner"
    class="chat-skel-v3" [NameOftheScreen]="'userChat'"></app-common-skeleton>
  <app-common-skeleton *ngIf="!chatLoadingIsOwner"
    class="chat-skel-v3" [NameOftheScreen]="'messageConversation'" [countforView]="2"></app-common-skeleton>
</div>
      <app-message-widget
        *ngIf="messagemodelflag"
        [embedded]="true"
        (closeMessageModel)="closeMessage($event)"
        [messageDataPass]="messageData">
      </app-message-widget>
    </section>

  </div>
</div>
